import { routePaths } from "@/App";
import { useToast } from "@/components/ui/use-toast";

import {
  deliverBulkOrder,
  deliverFailBulkOrder,
  deliverOrder,
  dropOffGather,
  getOrder,
  loadGather,
  loadOrder,
  markDeliveryFailed,
  markGatherFailed,
  uploadDeliveryProof,
} from "@/libs/api/requests";
import { mutationStore } from "@/libs/network/services/OfflineAsyncMutationStore";
import { isAxiosError } from "axios";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useTourDetailContext } from "../data/provider";

export function useTourStop(currentStopId: string) {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { toast } = useToast();
  const { stops, setContext, tour } = useTourDetailContext();

  const currentStop = useMemo(
    () => stops.find((stop) => stop.id === currentStopId),
    [currentStopId, stops],
  );

  const handleGatherDropoffFailed = useCallback(
    (parentId: string, taskId: string, inputs: { driverNote: string }) => {
      if (!currentStop) {
        return;
      }

      const currentStopState = currentStop.state;

      setContext({
        type: "updateTourStopState",
        stopId: taskId,
        newState: "canceled",
      });

      mutationStore.enqueueMutation({
        mutate: () =>
          markGatherFailed({
            id: parentId,
            driverNote: inputs.driverNote,
          }),
        resolve: () => {},
        reject: () => {
          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: currentStopState,
          });
          toast({
            title: t("toasts.deliveryFailed.title"),
            description: t("toasts.deliveryFailed.description"),
            variant: "error",
          });
        },
      });

      navigate(
        routePaths.createTourStopPath(tour.id, currentStopId, currentStop.type),
      );
    },
    [currentStop, currentStopId, navigate, setContext, t, toast, tour.id],
  );

  const handleGatherPickupFailed = useCallback(
    (parentId: string, taskId: string, inputs: { driverNote: string }) => {
      if (!currentStop) {
        return;
      }

      const currentStopState = currentStop.state;

      setContext({
        type: "updatePickupCanceled",
        taskId: taskId,
        parentId: parentId,
      });

      mutationStore.enqueueMutation({
        mutate: () =>
          markGatherFailed({
            id: parentId,
            driverNote: inputs.driverNote,
          }),
        resolve: () => {},
        reject: () => {
          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: currentStopState,
          });
          toast({
            title: t("toasts.deliveryFailed.title"),
            description: t("toasts.deliveryFailed.description"),
            variant: "error",
          });
        },
      });

      navigate(
        routePaths.createTourStopPath(tour.id, currentStopId, currentStop.type),
      );
    },
    [currentStop, currentStopId, navigate, setContext, t, toast, tour.id],
  );

  const handleDeliveryFailed = useCallback(
    (
      parentId: string,
      taskId: string,
      inputs: { abortReason: string; driverNote: string },
      isBulkTask?: boolean,
    ) => {
      if (!currentStop) {
        return;
      }

      const currentStopState = currentStop.state;

      setContext({
        type: "updateTourStopState",
        stopId: taskId,
        newState: "canceled",
      });

      const deliveryFailedPromise = isBulkTask
        ? deliverFailBulkOrder
        : markDeliveryFailed;

      mutationStore.enqueueMutation({
        mutate: () =>
          deliveryFailedPromise({
            id: parentId,
            abortReason: inputs.abortReason,
            driverNote: inputs.driverNote,
          }),
        resolve: () => {},
        reject: () => {
          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: currentStopState,
          });
          toast({
            title: t("toasts.deliveryFailed.title"),
            description: t("toasts.deliveryFailed.description"),
            variant: "error",
          });
        },
      });

      navigate(
        routePaths.createTourStopPath(tour.id, currentStopId, currentStop.type),
      );
    },
    [currentStop, currentStopId, navigate, setContext, t, toast, tour.id],
  );

  const loadTourStop = useCallback(
    (parentId: string, isScan?: boolean) => {
      if (!currentStop) return;

      const task = currentStop.items.find((item) => item.parentId === parentId);

      if (!task) return;

      toast({
        title: t("toasts.orderPickup.success"),
        variant: "success",
      });

      setContext({
        type: "updateLoaded",
        taskId: task.id,
        parentId,
      });

      mutationStore.enqueueMutation({
        mutate: () =>
          loadOrder(parentId).then(() => {
            getOrder({ orderId: parentId, isScan });
          }),
        resolve: () => {},
        reject: (error) => {
          // TODO: make this prettier. This is a workaround to update
          // the state of the matching delivery task
          // This should happen inside the reducer
          setContext({
            type: "new",
            newState: {
              loading: false,
              error: null,
              tour: tour,
              stops: stops.map((stop) => {
                return {
                  ...stop,
                  items: stop.items.map((item) => {
                    if (item.id === task.id) {
                      return {
                        ...item,
                        state: "loaded",
                      };
                    }

                    return item;
                  }),
                };
              }),
            },
          });

          setContext({
            type: "updateTourStopState",
            stopId: task.id,
            newState: "loaded",
          });

          if (isAxiosError(error) && error.response) {
            toast({
              title: t("toasts.orderPickup.error"),
              description: error.response.data.error,
              variant: "error",
            });
          }
        },
      });
    },
    [currentStop, setContext, stops, t, toast, tour],
  );

  const uploadDeliveryProofImage = useCallback(
    (base64: string, parentId: string, taskId: string) => {
      if (!currentStop) {
        return;
      }

      mutationStore.enqueueMutation({
        mutate: () =>
          uploadDeliveryProof({
            orderId: parentId,
            tourTaskId: taskId,
            base64: base64,
          }),
        resolve: () => {},
        reject: () => {
          // TODO: handle error
        },
      });
    },
    [currentStop],
  );

  const handleTourStopDelivery = useCallback(
    (
      taskId: string,
      parentId: string,
      inputs: {
        signature?: string;
        driverNote?: string;
        handedToName?: string;
        handedToAddress?: string;
        paymentType?: string;
        paymentCollected?: number;
      },
      isBulkTask?: boolean,
    ) => {
      if (!currentStop) return;

      toast({
        title: t("toasts.orderDelivery.success"),
        variant: "success",
      });

      setContext({
        type: "updateTourStopState",
        stopId: taskId,
        newState: "done",
      });

      const deliveryPromise = isBulkTask ? deliverBulkOrder : deliverOrder;

      mutationStore.enqueueMutation({
        mutate: () =>
          deliveryPromise({
            id: parentId,
            signature: inputs.signature,
            driverNote: inputs.driverNote,
            handedToName: inputs.handedToName,
            handedToAddress: inputs.handedToAddress,
            paymentCollected: inputs.paymentCollected ?? 0,
            paymentType: inputs.paymentType || undefined,
          }),
        resolve: () => {},
        reject: (error) => {
          if (
            isAxiosError(error) &&
            error.response &&
            error.response.data.error === "ERR_ILLEGAL_STATE" &&
            error.response.data.data ===
              "Attempted to deliver a Task that was not yet loaded"
          ) {
            toast({
              title: t("toasts.orderDelivery.error"),
              description: t("toasts.orderDelivery.notLoadedYet"),
              variant: "error",
            });
          } else {
            toast({
              title: t("toasts.orderDelivery.error"),
              variant: "error",
            });
          }

          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: "loaded",
          });
        },
      });

      navigate(
        routePaths.createTourStopPath(tour.id, currentStopId, currentStop.type),
      );
    },
    [currentStop, toast, t, setContext, navigate, tour.id, currentStopId],
  );

  const handleGatherStopLoad = useCallback(
    (taskId: string, parentId: string) => {
      if (!currentStop) return;

      const previousState = currentStop.state;

      setContext({
        type: "updateLoaded",
        taskId: taskId,
        parentId: parentId,
      });

      mutationStore.enqueueMutation({
        mutate: () => loadGather(parentId),
        resolve: () => {
          toast({
            title: t("toasts.gatherPickup.success"),
            variant: "success",
          });
        },
        reject: () => {
          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: previousState,
          });

          toast({
            title: t("toasts.gatherPickup.error"),
            variant: "error",
          });
        },
      });
    },
    [currentStop, setContext, t, toast],
  );

  const handleGatherDropOff = useCallback(
    (taskId: string, parentId: string, inputs: { driverNote?: string }) => {
      toast({
        title: t("toasts.gatherDropoff.success"),
        variant: "success",
      });

      setContext({
        type: "updateTourStopState",
        stopId: taskId,
        newState: "done",
      });

      mutationStore.enqueueMutation({
        mutate: () => dropOffGather(parentId, inputs),
        resolve: () => {
          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: "done",
          });
        },
        reject: () => {
          toast({
            title: t("toasts.gatherDropoff.error"),
            variant: "error",
          });

          setContext({
            type: "updateTourStopState",
            stopId: taskId,
            newState: "loaded",
          });
        },
      });
    },
    [setContext, t, toast],
  );

  return useMemo(
    () => ({
      currentStop,
      loadTourStop,
      handleGatherDropOff,
      handleGatherStopLoad,
      deliverFailTourStop: handleDeliveryFailed,
      deliverTourStop: handleTourStopDelivery,
      uploadDeliveryProofImage,
      handleGatherDropoffFailed,
      handleGatherPickupFailed,
    }),
    [
      currentStop,
      handleDeliveryFailed,
      handleGatherDropOff,
      handleGatherStopLoad,
      handleTourStopDelivery,
      loadTourStop,
      handleGatherDropoffFailed,
      handleGatherPickupFailed,
      uploadDeliveryProofImage,
    ],
  );
}
